import React from "react";
import { ConfigProvider, InputNumber } from "antd";
import type { FormInstance } from "antd";
import { LeftOutlined } from "@ant-design/icons";

import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  ToggleSwitch,
} from "../../../../components/shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import type { BannerIn } from "../types";
import styles from "../stylesDynamicBonuses.module.css";
import { ConfiguredBannerPreview } from "./BannerDesignField";
import ColorInput from "./ColorInput";
import ColorListInput from "./ColorListInput";
import {
  bannerThemePreset,
  getBannerThemeOptions,
  type BannerTheme,
} from "./bannerThemes";

type NamePath = (string | number)[];

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/;

const colorRule = {
  pattern: HEX_COLOR_PATTERN,
  message: "Use #RRGGBB or #RRGGBBAA",
};

const colorListRule = {
  validator: (_: unknown, value?: string[]) => {
    if (!value?.length || value.every((color) => HEX_COLOR_PATTERN.test(color))) {
      return Promise.resolve();
    }
    return Promise.reject(
      new Error("Each stop must be a valid #RRGGBB or #RRGGBBAA color"),
    );
  },
};

export type CustomizeBannerModalProps = {
  open: boolean;
  onClose: () => void;
  form: FormInstance;
  namePath: NamePath;
  itemNamePath: NamePath;
  selectedTheme: BannerTheme | "custom";
  onThemeChange: (theme: BannerTheme) => void;
  onEditImage?: () => void;
  enableWhenFormDisabled?: boolean;
};

const ShadowToggleField: React.FC<{
  name: NamePath;
  label?: string;
  initialValue?: boolean;
  ariaLabel?: string;
}> = ({
  name,
  label = AppStrings.PAM_DYNAMIC_BONUS_BANNER_SHADOW,
  initialValue,
  ariaLabel,
}) => (
  <div className={styles.customizeBannerShadowField}>
    <span className={styles.customizeBannerFieldLabel}>{label}</span>
    <Form.Item name={name} initialValue={initialValue} style={{ marginBottom: 0 }}>
      <ToggleSwitch tone="primary" ariaLabel={ariaLabel ?? label} />
    </Form.Item>
  </div>
);

const CustomizeBannerModal: React.FC<CustomizeBannerModalProps> = ({
  open,
  onClose,
  form,
  namePath,
  itemNamePath,
  selectedTheme,
  onThemeChange,
  onEditImage,
  enableWhenFormDisabled = false,
}) => {
  const itemPath = (...segments: NamePath): NamePath => [
    ...itemNamePath,
    ...segments,
  ];
  const brandPreset = bannerThemePreset("brand");

  const banner =
    (Form.useWatch(namePath, form) as BannerIn | undefined) ?? {};
  const detailsVisible = banner.details?.visible;
  const timerVisible = banner.timer?.visible;
  const claimVisible = banner.claim?.visible;
  const imageUrl = banner.image?.url;

  const themeOptions = [
    ...getBannerThemeOptions(),
    {
      value: "custom" as const,
      label: AppStrings.PAM_DYNAMIC_BONUS_BANNER_THEME_CUSTOM,
    },
  ];

  const colorListProps = {
    textAction: true as const,
    addLabel: AppStrings.PAM_DYNAMIC_BONUS_BANNER_ADD_COLOR,
  };

  const body = (
    <div className={styles.customizeBannerModalBody}>
      <div
        className={styles.customizeBannerPreview}
        data-testid="customize-banner-preview"
      >
        <p className={styles.bannerDesignPreviewLabel}>
          {AppStrings.PAM_DYNAMIC_BONUS_BANNER_PREVIEW_LABEL}
        </p>
        <div className={styles.bannerDesignPreview}>
          <ConfiguredBannerPreview banner={banner} />
        </div>
      </div>

      {/* Theme — field only, not a section card (Figma) */}
      <Form.Item
        label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_THEME_LABEL}
        className={styles.customizeBannerThemeField}
      >
        <Select
          value={selectedTheme}
          options={themeOptions}
          onChange={(value: BannerTheme | "custom") => {
            if (value === "custom") return;
            onThemeChange(value);
          }}
          aria-label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_THEME_LABEL}
        />
      </Form.Item>

      <section className={styles.customizeBannerSection}>
        <h3 className={styles.customizeBannerSectionTitle}>
          {AppStrings.PAM_DYNAMIC_BONUS_BANNER_BODY_SECTION}
        </h3>
        <div className={styles.customizeBannerPairGrid}>
          <Form.Item
            label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_BACKGROUND_LABEL}
            name={itemPath("background_color")}
            initialValue={brandPreset.backgroundColors}
            rules={[colorListRule]}
            tooltip="Add one color for solid, or multiple for a gradient"
          >
            <ColorListInput placeholder="#FC4233" {...colorListProps} />
          </Form.Item>
          <Form.Item
            label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_BORDER_LABEL}
            name={itemPath("border_color")}
            initialValue={brandPreset.borderColors}
            rules={[colorListRule]}
            tooltip="Add one color for solid, or multiple for a gradient"
          >
            <ColorListInput placeholder="#FD6F27" {...colorListProps} />
          </Form.Item>
        </div>
      </section>

      <section className={styles.customizeBannerSection}>
        <h3 className={styles.customizeBannerSectionTitle}>
          {AppStrings.PAM_DYNAMIC_BONUS_BANNER_PRIMARY_CONTENT_SECTION}
        </h3>
        <div className={styles.customizeBannerPairGrid}>
          <div className={styles.customizeBannerColorShadowRow}>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_TITLE_COLOR}
              name={itemPath("title", "color")}
              initialValue={brandPreset.titleColor || "#09090C"}
              rules={[colorRule]}
              className={styles.customizeBannerFlexField}
            >
              <ColorInput
                placeholder={brandPreset.titleColor || "#09090C"}
                data-testid="banner-title-color"
              />
            </Form.Item>
            <ShadowToggleField name={itemPath("title", "shadow")} />
          </div>
          <div className={styles.customizeBannerColorShadowRow}>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_DESCRIPTION_COLOR}
              name={itemPath("short_description", "color")}
              initialValue={brandPreset.shortDescriptionColor || "#25252F"}
              rules={[colorRule]}
              className={styles.customizeBannerFlexField}
            >
              <ColorInput
                placeholder={brandPreset.shortDescriptionColor || "#25252F"}
              />
            </Form.Item>
            <ShadowToggleField name={itemPath("short_description", "shadow")} />
          </div>
        </div>
        <div className={styles.customizeBannerPairGrid}>
          <div className={styles.customizeBannerColorShadowRow}>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_LONG_DESCRIPTION_COLOR}
              name={itemPath("long_description", "color")}
              initialValue={brandPreset.longDescriptionColor || "#25252F"}
              rules={[colorRule]}
              className={styles.customizeBannerFlexField}
            >
              <ColorInput
                placeholder={brandPreset.longDescriptionColor || "#25252F"}
              />
            </Form.Item>
            <ShadowToggleField name={itemPath("long_description", "shadow")} />
          </div>
          <div className={styles.customizeBannerImageField}>
            <div className={styles.customizeBannerImageLabelRow}>
              <span className={styles.customizeBannerFieldLabel}>
                {AppStrings.PAM_DYNAMIC_BONUS_BANNER_IMAGE_LABEL}
              </span>
              {onEditImage ? (
                <Button
                  htmlType="button"
                  intent="neutral"
                  variant="ghost"
                  className={styles.customizeTextAction}
                  onClick={onEditImage}
                >
                  {AppStrings.PAM_DYNAMIC_BONUS_BANNER_EDIT_IMAGE}
                </Button>
              ) : null}
            </div>
            <Input
              aria-label="Customize banner image URL"
              placeholder={
                AppStrings.PAM_DYNAMIC_BONUS_BANNER_IMAGE_PLACEHOLDER
              }
              data-testid="customize-banner-image-url"
              value={imageUrl ?? ""}
              onChange={(event) =>
                form.setFieldValue(
                  [...namePath, "image", "url"],
                  event.target.value,
                )
              }
            />
          </div>
        </div>
      </section>

      <section className={styles.customizeBannerSection}>
        <div className={styles.customizeBannerSectionHeader}>
          <h3 className={styles.customizeBannerSectionTitle}>
            {AppStrings.PAM_DYNAMIC_BONUS_BANNER_DETAILS_LINK_SECTION}
          </h3>
          <Form.Item
            name={itemPath("details", "visible")}
            initialValue
            style={{ marginBottom: 0 }}
          >
            <ToggleSwitch
              tone="primary"
              ariaLabel={
                AppStrings.PAM_DYNAMIC_BONUS_BANNER_DETAILS_LINK_SECTION
              }
            />
          </Form.Item>
        </div>
        {detailsVisible !== false ? (
          <div className={styles.customizeBannerColorShadowRow}>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_LINK_COLOR}
              name={itemPath("details", "color")}
              initialValue={brandPreset.detailsColor || "#09090C"}
              rules={[colorRule]}
              className={styles.customizeBannerFlexField}
            >
              <ColorInput placeholder={brandPreset.detailsColor || "#09090C"} />
            </Form.Item>
            <ShadowToggleField name={itemPath("details", "shadow")} />
          </div>
        ) : null}
      </section>

      <section className={styles.customizeBannerSection}>
        <div className={styles.customizeBannerSectionHeader}>
          <h3 className={styles.customizeBannerSectionTitle}>
            {AppStrings.PAM_DYNAMIC_BONUS_BANNER_COUNTDOWN_CHIP_SECTION}
          </h3>
          <Form.Item
            name={itemPath("timer", "visible")}
            initialValue
            style={{ marginBottom: 0 }}
          >
            <ToggleSwitch
              tone="primary"
              ariaLabel={
                AppStrings.PAM_DYNAMIC_BONUS_BANNER_COUNTDOWN_CHIP_SECTION
              }
            />
          </Form.Item>
        </div>
        {timerVisible !== false ? (
          <>
            <div className={styles.customizeBannerTripleGrid}>
              <Form.Item
                label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_TEXT_COLOR}
                name={itemPath("timer", "text_color")}
                initialValue={brandPreset.timerTextColor || "#09090C"}
                rules={[colorRule]}
              >
                <ColorInput
                  placeholder={brandPreset.timerTextColor || "#09090C"}
                />
              </Form.Item>
              <Form.Item
                label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_ICON_COLOR}
                name={itemPath("timer", "icon_color")}
                initialValue={brandPreset.timerIconColor || "#09090C"}
                rules={[colorRule]}
              >
                <ColorInput
                  placeholder={brandPreset.timerIconColor || "#09090C"}
                />
              </Form.Item>
              <ShadowToggleField
                name={itemPath("timer", "shadow")}
                label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_CHIP_SHADOW}
                initialValue={brandPreset.timerShadow}
              />
            </div>
            <div className={styles.customizeBannerTripleGrid}>
              <Form.Item
                label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_BACKGROUND_COLOR}
                name={itemPath("timer", "background_color")}
                initialValue="#FFFFFF80"
                rules={[colorRule]}
              >
                <ColorInput placeholder="#FFFFFF80" />
              </Form.Item>
              <Form.Item
                label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_BORDER_COLOR}
                name={itemPath("timer", "border_color")}
                initialValue={brandPreset.timerBorderColor}
                rules={[colorRule]}
              >
                <ColorInput placeholder="#FD6F27" />
              </Form.Item>
              <Form.Item
                label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_BORDER_WIDTH}
                name={itemPath("timer", "border_width")}
                initialValue={brandPreset.timerBorderWidth}
                className={styles.customizeBannerNarrowField}
              >
                <InputNumber
                  className={styles.numberInput}
                  controls={false}
                  min={0}
                  step={0.5}
                />
              </Form.Item>
            </div>
          </>
        ) : null}
      </section>

      <section className={styles.customizeBannerSection}>
        <div className={styles.customizeBannerSectionHeader}>
          <h3 className={styles.customizeBannerSectionTitle}>
            {AppStrings.PAM_DYNAMIC_BONUS_BANNER_CLAIM_BUTTON_SECTION}
          </h3>
          <Form.Item
            name={itemPath("claim", "visible")}
            style={{ marginBottom: 0 }}
          >
            <ToggleSwitch
              tone="primary"
              ariaLabel={
                AppStrings.PAM_DYNAMIC_BONUS_BANNER_CLAIM_BUTTON_SECTION
              }
            />
          </Form.Item>
        </div>
        {claimVisible ? (
          <div className={styles.customizeBannerClaimGrid}>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_CLAIM_TEXT}
              name={itemPath("claim", "text", "en")}
              className={styles.customizeBannerFieldFull}
            >
              <Input placeholder="Claim" />
            </Form.Item>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_TEXT_COLOR}
              name={itemPath("claim", "text_color")}
              initialValue="#09090C"
              rules={[colorRule]}
            >
              <ColorInput placeholder="#09090C" />
            </Form.Item>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_BORDER_COLOR}
              name={itemPath("claim", "border_color")}
              initialValue={brandPreset.claimBorderColor}
              rules={[colorRule]}
            >
              <ColorInput placeholder="#FD6F27" />
            </Form.Item>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_BORDER_WIDTH}
              name={itemPath("claim", "border_width")}
            >
              <InputNumber
                className={styles.numberInput}
                controls={false}
                min={0}
                step={0.5}
              />
            </Form.Item>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_BACKGROUND_COLOR}
              name={itemPath("claim", "background_color")}
              initialValue={brandPreset.claimBackgroundColors}
              rules={[colorListRule]}
              tooltip="Add one color for solid, or multiple for a gradient"
              className={styles.customizeBannerFieldFull}
            >
              <ColorListInput placeholder="#FC4233" {...colorListProps} />
            </Form.Item>
            <ShadowToggleField name={itemPath("claim", "shadow")} />
          </div>
        ) : null}
      </section>
    </div>
  );

  const modal = (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      width={720}
      footer={null}
      destroyOnHidden
      closable={false}
      className={styles.customizeBannerModal}
      data-testid="customize-banner-modal"
      title={
        <div className={styles.customizeBannerModalTitle}>
          <button
            type="button"
            className={styles.customizeBannerModalBack}
            onClick={onClose}
            aria-label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_BACK}
          >
            <LeftOutlined />
          </button>
          <span>{AppStrings.PAM_DYNAMIC_BONUS_BANNER_CUSTOMIZE}</span>
        </div>
      }
    >
      {body}
    </Modal>
  );

  if (enableWhenFormDisabled) {
    return (
      <ConfigProvider componentDisabled={false}>{modal}</ConfigProvider>
    );
  }

  return modal;
};

export default CustomizeBannerModal;
